import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Form, 
  Button, 
  InputGroup,
  Pagination
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faCheck } from '@fortawesome/free-solid-svg-icons';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from 'utils/axiosInterceptors';
import { carModelBodyTypeLabel } from 'components/vehicles/CarModelBodyTypeSelect';

// Types
interface CarMake {
  carMakeId: number;
  name: string;
}

interface CarModel {
  carModelId: number;
  name: string;
  carMakeId: number;
}

interface Vehicle {
  vehicleId: string;
  plateNumber: string;
  conductionNumber: string;
  carMakeId: number;
  carModelId: number;
  year: number;
  customerId: string;
  customerName: string;
  vehicleImage: string;
  lastServicedOn: string;
  hasServiceInProgress: boolean;
  // Additional fields that might be present
  CarMake?: string;
  CarModel?: string;
  bodyType?: string;
}

const Vehicles: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [carMakes, setCarMakes] = useState<CarMake[]>([]);
  const [carModels, setCarModels] = useState<CarModel[]>([]);
  const [filteredModels, setFilteredModels] = useState<CarModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;
  
  // Search and filter states - initialize from URL params
  const [searchKeyword, setSearchKeyword] = useState(searchParams.get('search') || '');
  const [selectedMake, setSelectedMake] = useState(searchParams.get('carMakeId') || '');
  const [selectedModel, setSelectedModel] = useState(searchParams.get('carModelId') || '');
  
  // Sorting states - initialize from URL params
  const [orderBy, setOrderBy] = useState(searchParams.get('orderBy') || 'lastServicedOn');
  const [orderByDesc, setOrderByDesc] = useState(searchParams.get('orderByDesc') !== null ? searchParams.get('orderByDesc') === 'true' : true);



  // Fetch data functions
  const fetchCarMakes = async () => {
    try {
      const response = await axiosInstance.get('/api/v1/carMakes?page=1&pageSize=999');
      // Check if response has data property or is direct array
      const makes = response.data.data || response.data;
      setCarMakes(makes);
    } catch (error) {
      console.error('Error fetching car makes:', error);
    }
  };

  const fetchCarModels = async () => {
    try {
      const response = await axiosInstance.get('/api/v1/carModels?page=1&pageSize=999');
      // Check if response has data property or is direct array
      const models = response.data.data || response.data;
      setCarModels(models);
      setFilteredModels(models);
    } catch (error) {
      console.error('Error fetching car models:', error);
    }
  };

  const fetchVehicles = useCallback(async (page = 1, search = '', carMakeId = '', carModelId = '') => {
    try {
      setIsLoading(true);
      
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());
      
      if (search) params.append('search', search);
      if (carMakeId) params.append('carMakeId', carMakeId);
      if (carModelId) params.append('carModelId', carModelId);
      
      // Add sorting parameters
      params.append('orderBy', orderBy);
      params.append('orderByDesc', orderByDesc.toString());
      
      const response = await axiosInstance.get(`/api/v1/vehicles?${params.toString()}`);
      
      // Check if response has data property or is direct array
      const vehiclesData = response.data.data || response.data;
      const apiTotalPages = response.data.pageCount || 1;
      
      // Enrich vehicle data with make and model names
      const enrichedVehicles = vehiclesData.map((vehicle: Vehicle) => {
        const make = carMakes.find(m => m.carMakeId === vehicle.carMakeId);
        const model = carModels.find(m => m.carModelId === vehicle.carModelId);
        return {
          ...vehicle,
          CarMake: make?.name || 'Unknown',
          CarModel: model?.name || 'Unknown'
        };
      });
        
      setVehicles(enrichedVehicles);
      setCurrentPage(page);
      setTotalPages(apiTotalPages);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setIsLoading(false);
    }
  }, [carMakes, carModels, pageSize, orderBy, orderByDesc]);



  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([fetchCarMakes(), fetchCarModels()]);
    };
    loadInitialData();
  }, []);

  // Initialize filtered models based on URL params
  useEffect(() => {
    if (carModels.length > 0 && selectedMake) {
      const filtered = carModels.filter(model => model.carMakeId === parseInt(selectedMake));
      setFilteredModels(filtered);
    } else if (carModels.length > 0) {
      setFilteredModels(carModels);
    }
  }, [carModels, selectedMake]);

  // Fetch vehicles when dependencies change
  useEffect(() => {
    if (carMakes.length > 0 && carModels.length > 0) {
      fetchVehicles(currentPage, searchKeyword, selectedMake, selectedModel);
    }
  }, [carMakes, carModels, searchKeyword, selectedMake, selectedModel, orderBy, orderByDesc, currentPage, fetchVehicles]);

  // Handle make selection change
  const handleMakeChange = (makeId: string) => {
    setSelectedMake(makeId);
    setSelectedModel(''); // Reset model selection
    
    // Update URL parameters
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (makeId) {
        newParams.set('carMakeId', makeId);
      } else {
        newParams.delete('carMakeId');
      }
      newParams.delete('carModelId'); // Reset model selection in URL
      newParams.delete('page'); // Reset to page 1
      return newParams;
    });
    
    if (makeId) {
      const filtered = carModels.filter(model => model.carMakeId === parseInt(makeId));
      setFilteredModels(filtered);
    } else {
      setFilteredModels(carModels);
    }
    
    setCurrentPage(1);
  };

  // Handle model selection change
  const handleModelChange = (modelId: string) => {
    setSelectedModel(modelId);
    
    // Update URL parameters
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (modelId) {
        newParams.set('carModelId', modelId);
      } else {
        newParams.delete('carModelId');
      }
      newParams.delete('page'); // Reset to page 1
      return newParams;
    });
    
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (page > 1) {
        newParams.set('page', page.toString());
      } else {
        newParams.delete('page');
      }
      return newParams;
    });
    setCurrentPage(page);
  };

  // Handle search
  const handleSearch = () => {
    // Update URL parameters
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (searchKeyword) {
        newParams.set('search', searchKeyword);
      } else {
        newParams.delete('search');
      }
      if (selectedMake) {
        newParams.set('carMakeId', selectedMake);
      } else {
        newParams.delete('carMakeId');
      }
      if (selectedModel) {
        newParams.set('carModelId', selectedModel);
      } else {
        newParams.delete('carModelId');
      }
      // Reset to page 1 when searching
      newParams.delete('page');
      return newParams;
    });
    
    setCurrentPage(1);
    fetchVehicles(1, searchKeyword, selectedMake, selectedModel);
  };

  // Handle column sorting
  const handleSort = (columnKey: string) => {
    let newOrderByDesc = false;
    
    if (orderBy === columnKey) {
      // If clicking the same column, toggle sort direction
      newOrderByDesc = !orderByDesc;
    } else {
      // If clicking a different column, set new column and default to ascending
      newOrderByDesc = false;
    }
    
    // Update URL parameters
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('orderBy', columnKey);
      newParams.set('orderByDesc', newOrderByDesc.toString());
      // Reset to page 1 when sorting changes
      newParams.delete('page');
      return newParams;
    });
    
    // Update state
    setOrderBy(columnKey);
    setOrderByDesc(newOrderByDesc);
    setCurrentPage(1);
  };



  // Helper function to render sortable header
  const renderSortableHeader = (title: string, columnKey: string) => (
    <div 
      style={{ cursor: 'pointer', userSelect: 'none' }} 
      onClick={() => handleSort(columnKey)}
      className="d-flex align-items-center"
    >
      <span>{title}</span>
      {orderBy === columnKey && (
        <span className="ms-1">
          {orderByDesc ? '↓' : '↑'}
        </span>
      )}
    </div>
  );

  // Table columns
  const columns: ColumnDef<Vehicle>[] = [
    {
      id: 'plateNumber',
      header: () => renderSortableHeader('Plate Number/Conduction Number', 'plateNumber'),
      accessorKey: 'plateNumber',
      cell: ({ row }) => (
        <Button
          variant="link"
          className="p-0 text-decoration-underline"
          onClick={() => navigate(`/vehicles/${row.original.vehicleId}`)}
        >
          {row.original.plateNumber || row.original.conductionNumber}
        </Button>
      )
    },
    {
      id: 'make',
      header: () => renderSortableHeader('Make', 'make'),
      accessorKey: 'CarMake',
      cell: ({ row }) => (
        <span>{row.original.CarMake}</span>
      )
    },
    {
      id: 'model',
      header: () => renderSortableHeader('Model', 'model'),
      accessorKey: 'CarModel',
      cell: ({ row }) => (
        <span>{row.original.CarModel}</span>
      )
    },
    {
      id: 'bodyType',
      header: 'Body type',
      accessorKey: 'bodyType',
      cell: ({ row }) => (
        <span>{carModelBodyTypeLabel(row.original.bodyType) || 'N/A'}</span>
      )
    },
    {
      id: 'year',
      header: () => renderSortableHeader('Year', 'year'),
      accessorKey: 'year',
      cell: ({ row }) => (
        <span>{row.original.year}</span>
      )
    },
    {
      id: 'customer',
      header: 'Customer Name',
      accessorKey: 'customerName',
      cell: ({ row }) => (
        <span>{row.original.customerName}</span>
      )
    },
    {
      id: 'lastServiceDate',
      header: () => renderSortableHeader('Last Service Date', 'lastServicedOn'),
      accessorKey: 'lastServicedOn',
      cell: ({ row }) => {
        const formatDate = (dateString: string) => {
          if (!dateString) return 'N/A';
          try {
            const date = new Date(dateString);
            return date.toLocaleDateString();
          } catch {
            return 'Invalid Date';
          }
        };
        
        return (
          <span>{formatDate(row.original.lastServicedOn)}</span>
        );
      }
    },
    {
      id: 'inProgress',
      header: () => renderSortableHeader('InProgress', 'hasServiceInProgress'),
      accessorKey: 'hasServiceInProgress',
      cell: ({ row }) => (
        <div className="text-center">
          {row.original.hasServiceInProgress && (
            <FontAwesomeIcon icon={faCheck} className="text-success" />
          )}
        </div>
      )
    }
  ];

  const table = useAdvanceTable({
    data: vehicles,
    columns,
    pagination: true, // Enable pagination to control pageSize
    sortable: false, // We handle sorting manually
    selection: false,
    pageSize: Math.max(vehicles.length, 25), // Always show at least 25 rows
    initialState: {
      pagination: {
        pageSize: Math.max(vehicles.length, 25),
        pageIndex: 0
      }
    }
  });

  // Update table pageSize when vehicles data changes
  useEffect(() => {
    if (vehicles.length > 0) {
      table.setPageSize(Math.max(vehicles.length, 25));
    }
  }, [vehicles.length, table]);



  return (
    <div>
      <Card>
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">Vehicles</h5>
            </Col>
            <Col xs="auto" className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => navigate('/car-makes')}
              >
                Browse makes
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate('/vehicles/create')}
              >
                Add Vehicle
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {/* Search and Filter Section */}
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Search</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Search vehicles..."
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Button variant="outline-secondary" onClick={handleSearch}>
                    <FontAwesomeIcon icon={faSearch} />
                  </Button>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Car Make</Form.Label>
                <Form.Select
                  value={selectedMake}
                  onChange={(e) => handleMakeChange(e.target.value)}
                >
                  <option value="">All Makes</option>
                  {carMakes.map(make => (
                    <option key={make.carMakeId} value={make.carMakeId}>
                      {make.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Car Model</Form.Label>
                <Form.Select
                  value={selectedModel}
                  onChange={(e) => handleModelChange(e.target.value)}
                  disabled={!selectedMake}
                >
                  <option value="">All Models</option>
                  {filteredModels.map(model => (
                    <option key={model.carModelId} value={model.carModelId}>
                      {model.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Table */}
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading vehicles...</p>
            </div>
          ) : (
            <div>
              <div className="mb-2 text-muted small">
                Displaying page {currentPage} of {totalPages} ({vehicles.length} vehicles)
              </div>
              
              <AdvanceTableProvider {...table}>
                <AdvanceTable
                  headerClassName="bg-200 text-nowrap align-middle"
                  bodyClassName="align-middle"
                  tableProps={{
                    size: 'sm',
                    striped: true,
                    hover: true
                  }}
                />
              </AdvanceTableProvider>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <Pagination.First 
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev 
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === currentPage}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    })}
                    
                    <Pagination.Next 
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last 
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </div>
          )}





          {!isLoading && vehicles.length === 0 && (
            <div className="text-center py-5 text-muted">
              <p>No vehicles found matching your criteria.</p>
            </div>
          )}
        </Card.Body>
      </Card>


    </div>
  );
};

export default Vehicles; 